import { useRef, useState } from "react";
import { useAppDispatch, useAppSelector } from "../../../../hooks";
import {
  getMeAsync,
  selectChangeAvatarLoading,
  selectUser,
  updateProfileAvatarAsync,
} from "../../../../store/auth/authSlice";
import UserDefaultAvatar from "./../../../../assets/images/blank-avatar.png";
import AvatarEditor from "react-avatar-editor";
import { convertBlobToFile } from "../../../../utils/file";
import { BASE_URL } from "../../../../apis";
import EditIcon from "./../../../../assets/icons/pencil-square.svg?react";
import { Button, Typography, Modal, useDjunoDesign } from "djuno-design";
import { ShineBorder } from "../../../general/ShineBorder";

const UserAvatar: React.FunctionComponent = () => {
  const authUser = useAppSelector(selectUser);
  const changeAvatarLoading = useAppSelector(selectChangeAvatarLoading);
  const dispatch = useAppDispatch();

  const {
    theme: { mode },
  } = useDjunoDesign();

  const imageInputRef = useRef<HTMLInputElement>(null);
  const editor = useRef<AvatarEditor>(null);

  const [loadLoading, setLoadLoading] = useState<boolean>(false);
  const [selectedImage, setSelectedImage] = useState<File | null>(null);
  const [scale, setScale] = useState<number>(1);
  const [rotate, setRotate] = useState<number>(0);

  const handleSelectImage = () => imageInputRef.current?.click();

  const handleSelectedImage = (event: React.ChangeEvent<HTMLInputElement>) => {
    setLoadLoading(true);
    const file = event.target.files?.[0];
    if (file) {
      setSelectedImage(file);
    }
  };

  const handleUploadImage = async () => {
    if (editor && selectedImage && !changeAvatarLoading && !loadLoading) {
      const canvasScaled = editor.current?.getImageScaledToCanvas();
      if (canvasScaled) {
        const dataUrl = canvasScaled.toDataURL();
        const res = await fetch(dataUrl);
        const blob = await res.blob();
        const img = convertBlobToFile(blob, selectedImage.name);
        dispatch(
          updateProfileAvatarAsync({
            img,
          })
        ).then((action) => {
          if (action.type === "auth/update-profile-avatar/fulfilled") {
            setSelectedImage(null);
            dispatch(getMeAsync({}));
          }
        });
      }
    }
  };

  return (
    <div className="">
      <div className="w-20 h-20 aspect-square rounded-full overflow-hidden shadow group relative">
        <ShineBorder shineColor={["#0074E5", "#A07CFE", "#FFBE7B"]} />
        <div
          className="absolute top-0 left-0 bottom-0 right-0 bg-white/30 dark:bg-black/10 backdrop-blur-[4px] justify-center items-center hidden group-hover:flex transition-all duration-200 cursor-pointer"
          onClick={handleSelectImage}
        >
          <EditIcon className="w-6 h-6 text-secondary-800 dark:text-secondary-800" />
        </div>
        <img
          src={
            authUser?.AvatarLink !== null
              ? BASE_URL + authUser?.AvatarLink
              : UserDefaultAvatar
          }
          alt={authUser?.FullName}
          className="w-full h-full cursor-pointer"
        />
      </div>
      <input
        ref={imageInputRef}
        type="file"
        hidden
        accept="image/*"
        onChange={handleSelectedImage}
      />
      <Modal
        isOpen={selectedImage !== null}
        title="Update profile image"
        contentClassName="!w-[350px]"
        onClose={() => {
          setSelectedImage(null);
          setScale(1);
          setRotate(0);
          setLoadLoading(false);
          if (imageInputRef.current) {
            imageInputRef.current.value = "";
          }
        }}
      >
        <div className="w-full min-h-[250px]">
          {selectedImage && (
            <div className="w-full flex flex-col gap-4 mt-5">
              <div className="w-full flex justify-center">
                <AvatarEditor
                  ref={editor}
                  image={selectedImage}
                  width={280}
                  height={280}
                  border={0}
                  color={
                    mode === "light" ? [255, 255, 255, 0.3] : [0, 0, 0, 0.3]
                  }
                  scale={scale}
                  rotate={rotate}
                  borderRadius={140}
                  onLoadSuccess={() => setLoadLoading(false)}
                />
              </div>
              <div className="range flex flex-1 items-center gap-3">
                <Typography.Text className="!text-sm">Scale: </Typography.Text>
                <input
                  type="range"
                  className="w-full cursor-pointer outline-none"
                  value={scale}
                  onChange={(e) => setScale(+e.target.value)}
                  min="0.5"
                  max="4"
                  step="0.1"
                />
                <Typography.Text className="!text-sm !w-10">
                  {scale}
                </Typography.Text>
              </div>
              <div className="range flex flex-1 items-center gap-3">
                <Typography.Text className="!text-sm">Rotate: </Typography.Text>
                <input
                  type="range"
                  className="w-full cursor-pointer outline-none"
                  value={rotate}
                  onChange={(e) => setRotate(+e.target.value)}
                  min="0"
                  max="360"
                  step="1"
                />
                <Typography.Text className="!text-sm !w-10">
                  {rotate}
                </Typography.Text>
              </div>
            </div>
          )}
        </div>
        <div className="mt-4 flex justify-end">
          <Button
            onClick={handleUploadImage}
            loading={changeAvatarLoading || loadLoading}
            uiType="primary"
          >
            Update
          </Button>
        </div>
      </Modal>
    </div>
  );
};

export default UserAvatar;
